import { generateItems } from "game-common/item/item"
import { randomAppearanceFor } from "game-common/lpc/lpc"
import { Appearance, Entity, Inventory, pctChance } from "game-common/models"
import { degrees_to_radians } from "game-common/util"
import { Container, Graphics } from "pixi.js"

import { LpcComposite } from "../../sprites/lpc_composite"
import { FlameBackdrop } from "../flame_backdrop"
import { BLAZE, CREAM, EMBER, Sparks, flicker, glowSprite } from "../ember_atmosphere"
import { BuilderPage, PageId } from "./page"
import { GridLayout, cellPosition, layoutGrid, pageSlice } from "./grid"
import {
    ACCENT,
    BODY_H,
    BODY_Y,
    PALETTE_W,
    PALETTE_X,
    STAGE_W,
    STAGE_X,
    TEXT_DIM,
    TRAITS_W,
    TRAITS_X,
} from "./theme"
import { TraitId, TraitSection, applyTraitChoice, describeTrait, describeTraits, reconcileForSex } from "./traits"
import { ColorChip, LabelChip, Pager, TraitRow, drawGlassPanel, fitText, sectionHeading, text } from "./widgets"

// The character forge: the trait list on the left, the character on a lit stage in the middle, and
// the palette for whichever trait is open on the right.
//
// Two things drive the design. The character always faces the viewer -- you are looking at the face
// you are building, not at whichever way the mouse last pointed. And every trait opens as a PALETTE:
// all of its choices laid out at once as chips and colour swatches, one click each, instead of a
// pair of arrows to cycle through.

const PANEL_PAD = 8
const ROW_H = 36
const ROW_GAP = 3

const PALETTE_PAD = 14
const paletteInnerWidth = () => PALETTE_W - PALETTE_PAD * 2
const PALETTE_TITLE_H = 38

const STYLE_CELL_H = 24
const STYLE_MIN_W = 76
const COLOR_CELL = 24
const GRID_GAP = 6
const SECTION_HEAD_H = 14
const SECTION_GAP = 10
const PAGER_H = 22

// The stage. The composite is drawn from the centre of its 64x64 frame, and a standing LPC figure's
// feet sit about 28px below that centre.
const COMPOSITE_SCALE = 3.4
const FEET_OFFSET = 28 * COMPOSITE_SCALE
const FEET_Y = BODY_Y + 252
const STAGE_CENTER_X = STAGE_X + STAGE_W / 2
const FLAME_H = 132

/** The character's little hop when a change lands, and how long the stage light flares with it. */
const POP_MS = 280
const FLARE_MS = 520
/** How long the chip you just picked rings for. */
const PULSE_MS = 620

/** The three columns arrive one after another: what you can change, who you are, what you can pick. */
const COLUMN_STAGGER = 90

export class BuildPage extends BuilderPage {
    readonly pageId: PageId = "build"
    readonly title = "Forge your character"
    readonly hint = "Pick a trait, then pick from its palette"

    // A private copy of the player: nothing the screen does touches the live entity until the
    // character is submitted.
    private entity: Entity
    private composite: LpcComposite
    private flame: FlameBackdrop

    private openTrait: TraitId = "sex"
    // What the composite was last baked from. A paperdoll rebuild is debounced and then swaps the
    // whole sprite out, so it is only asked for when the look really changed -- opening a trait or
    // paging a palette repaints the screen but leaves the character exactly as it was.
    private bakedLook?: string
    // Palettes bigger than one page remember where each trait was left.
    private pageIndex: Partial<Record<TraitId, number>> = {}

    // The stage's lights, for the per-frame flicker, and the layer the sparks fly in. The layer
    // outlives repaints (like the composite and the flame) so a burst is not cut off by the very
    // repaint the click that caused it asked for.
    private lights?: { back: Container; pool: Container; spot: Container }
    private fx = new Container()
    private sparks = new Sparks()
    private ms = 0
    // The baked sprite the composite was showing last frame. A paperdoll rebuild is debounced and
    // swaps the sprite out when it lands, so a change of sprite IS the moment the new look appears.
    private shownSprite?: unknown
    private popMs = POP_MS
    private flareMs = FLARE_MS
    private lastChoice?: { trait: TraitId; kind: string; value: string; at: number }
    private pulse?: { g: Graphics; x: number; y: number; w: number; h: number; ms: number }

    constructor(player: Entity) {
        super()
        this.entity = this.cloneForBuilder(player)
        // Particles only: its own glow is three stacked ellipses sized for the roster card, which banded
        // into visible rings across this stage. The stage's soft lights do that job here.
        this.flame = new FlameBackdrop(STAGE_W - 24, FLAME_H, { glow: false })
    }

    // Deep enough that editing the draft can't reach back into the live player's inventory (a
    // shallow copy shares the items array, so a new shirt would land on the player mid-creation).
    private cloneForBuilder = (player: Entity): Entity => {
        const entity = new Entity()
        entity.movement.angle = degrees_to_radians(90)
        entity.weapon = { weaponType: "none" }
        entity.appearance = { ...(player.appearance || randomAppearanceFor(undefined, "human", "male")) }
        entity.inventory = {
            items: (player.inventory?.items || []).map(item => ({
                ...item,
                itemTypeMeta: item.itemTypeMeta ? { ...item.itemTypeMeta } : undefined,
            })),
            abilities: { ...(player.inventory?.abilities || {}) },
            lastUpdateTs: 0,
        }

        const appearance = entity.appearance
        appearance.race = "human"
        // The eye layer is what the "Eyes" palette tints; an appearance that arrived without one has
        // nothing to colour.
        appearance.eyes = appearance.eyes || "normal"
        appearance.eyesColor = appearance.eyesColor || "brown"
        appearance.noseColor = appearance.bodyColor
        if (entity.inventory.items.length < 1) {
            entity.inventory.items = this.rollOutfit(entity)
        }
        reconcileForSex(entity)
        return entity
    }

    private rollOutfit = (entity: Entity) =>
        generateItems(entity.appearance.sex, {
            pants: true,
            shirt: entity.appearance.sex === "male" ? pctChance(60) : true,
            shoe: true,
            weapon: true,
        })

    getAppearance = (): Appearance => this.entity.appearance
    getInventory = (): Inventory => this.entity.inventory

    animate(dtMs: number) {
        this.ms += dtMs
        // A low, steady hearth glow at the character's feet -- alive, but never competing with the
        // character for attention.
        this.flame.update(dtMs, 0.45)
        this.sparks.update(dtMs)

        // A new look has just landed on the stage: give it a beat.
        const sprite = (this.composite as any)?.sprite
        if (sprite && sprite !== this.shownSprite) {
            if (this.shownSprite) {
                this.popMs = 0
                this.flareMs = 0
                // Thrown up from the floor around the figure and drawn BEHIND it: embers kicked up
                // by the change, framing the new silhouette. (From the chest, white-hot sparks over
                // a pale shirt simply vanished.)
                this.sparks.throw(this.fx, STAGE_CENTER_X, FEET_Y - 6, 18, 150, 170)
            }
            this.shownSprite = sprite
        }
        this.popMs = Math.min(POP_MS, this.popMs + dtMs)
        this.flareMs = Math.min(FLARE_MS, this.flareMs + dtMs)
        if (this.composite) {
            const hop = Math.sin(Math.PI * (this.popMs / POP_MS))
            this.composite.scale.set(COMPOSITE_SCALE * (1 + 0.05 * hop))
            this.composite.y = FEET_Y - FEET_OFFSET * (1 + 0.05 * hop)
        }

        const flare = 1 - this.flareMs / FLARE_MS
        if (this.lights) {
            this.lights.pool.alpha = 0.34 * (1 + 0.14 * flicker(this.ms, 1.3) + 0.9 * flare)
            this.lights.back.alpha = 0.2 * (1 + 0.08 * flicker(this.ms, 3.7) + 0.6 * flare)
            this.lights.spot.alpha = 0.09 * (1 + 0.5 * flare)
        }

        const pulse = this.pulse
        if (pulse) {
            pulse.ms += dtMs
            const k = Math.min(1, pulse.ms / PULSE_MS)
            const grow = 2 + 7 * (1 - Math.pow(1 - k, 3))
            pulse.g.clear()
            if (k < 1) {
                pulse.g.lineStyle(1.5, ACCENT, 0.8 * (1 - k))
                pulse.g.drawRoundedRect(pulse.x - grow, pulse.y - grow, pulse.w + grow * 2, pulse.h + grow * 2, 6 + grow)
                pulse.g.lineStyle(0)
            } else {
                this.pulse = undefined
            }
        }
    }

    tearDown() {
        this.openTrait = "sex"
        this.sparks.clear()
    }

    // Detach the pieces that outlive a repaint before the rest of the page is disposed.
    protected wipe() {
        if (this.composite?.parent) {
            this.composite.parent.removeChild(this.composite)
        }
        if (this.flame?.parent) {
            this.flame.parent.removeChild(this.flame)
        }
        if (this.fx.parent) {
            this.fx.parent.removeChild(this.fx)
        }
        this.pulse = undefined
        super.wipe()
    }

    build() {
        this.wipe()
        this.reveal(this.drawTraitList(), 0)
        this.reveal(this.drawStage(), COLUMN_STAGGER)
        this.reveal(this.drawPalette(), COLUMN_STAGGER * 2)
        this.drawNav()
    }

    private drawTraitList(): Container {
        const column = new Container()
        this.addChild(column)
        const panel = new Graphics()
        drawGlassPanel(panel, TRAITS_X, BODY_Y, TRAITS_W, BODY_H)
        column.addChild(panel)

        const views = describeTraits(this.entity)
        const listH = views.length * ROW_H + (views.length - 1) * ROW_GAP
        let y = BODY_Y + Math.max(PANEL_PAD, (BODY_H - listH) / 2)

        views.forEach(view => {
            const row = new TraitRow({
                icon: view.icon,
                label: view.label,
                summary: view.summary,
                swatch: view.swatch,
                width: TRAITS_W - PANEL_PAD * 2,
                height: ROW_H,
                selected: view.id === this.openTrait,
                onClick: () => {
                    this.openTrait = view.id
                    this.host.refresh()
                },
            })
            row.x = TRAITS_X + PANEL_PAD
            row.y = y
            column.addChild(row)
            y += ROW_H + ROW_GAP
        })
        return column
    }

    private drawStage(): Container {
        const column = new Container()
        this.addChild(column)
        const panel = new Graphics()
        drawGlassPanel(panel, STAGE_X, BODY_Y, STAGE_W, BODY_H)
        column.addChild(panel)

        // The light the character stands in: a warm wash behind them, a pool of firelight at their
        // feet, and a faint cool-white fall from above that picks out the figure. Soft sprites, so
        // there are no rings -- the stacked ellipses this replaced banded visibly.
        const stage = new Container()
        // Clip the light to the panel: the pool is wider than the stage and would spill over the
        // columns either side of it.
        const clip = new Graphics()
        clip.beginFill(0xffffff, 1)
        clip.drawRoundedRect(STAGE_X + 1, BODY_Y + 1, STAGE_W - 2, BODY_H - 2, 9)
        clip.endFill()
        stage.mask = clip
        column.addChild(clip)
        const back = glowSprite(BLAZE, 280, 330, 0.2)
        back.position.set(STAGE_CENTER_X, FEET_Y - 60)
        const pool = glowSprite(EMBER, 250, 120, 0.34)
        pool.position.set(STAGE_CENTER_X, FEET_Y + 4)
        const spot = glowSprite(CREAM, 150, 380, 0.09)
        spot.position.set(STAGE_CENTER_X, BODY_Y + 120)
        stage.addChild(back, pool, spot)
        column.addChild(stage)
        this.lights = { back, pool, spot }

        this.flame.x = STAGE_X + 12
        this.flame.y = BODY_Y + BODY_H - FLAME_H - 6
        column.addChild(this.flame)

        // Ground shadow, so the figure stands on something.
        const floor = new Graphics()
        floor.beginFill(0x000000, 0.45)
        floor.drawEllipse(STAGE_CENTER_X, FEET_Y + 2, 46, 11)
        floor.endFill()
        floor.beginFill(0x000000, 0.3)
        floor.drawEllipse(STAGE_CENTER_X, FEET_Y + 2, 62, 15)
        floor.endFill()
        column.addChild(floor)

        // Between the floor and the figure: where the sparks of a change fly.
        column.addChild(this.fx)

        if (!this.composite) {
            this.composite = new LpcComposite("")
            this.composite.scale.set(COMPOSITE_SCALE)
        }
        const look = this.lookSignature()
        if (look !== this.bakedLook) {
            this.bakedLook = look
            this.composite.updatePaperdoll(this.entity)
        }
        // Always facing the viewer: the angle is pinned to "front" and nothing turns it.
        this.composite.doMovement(this.entity)
        this.composite.x = STAGE_CENTER_X
        this.composite.y = FEET_Y - FEET_OFFSET
        column.addChild(this.composite)

        const caption = text("Bound for the West", { role: "compactCaption", color: TEXT_DIM, display: true, size: 11 })
        caption.x = Math.round(STAGE_CENTER_X - caption.width / 2)
        caption.y = BODY_Y + BODY_H - 21
        caption.alpha = 0.8
        column.addChild(caption)
        return column
    }

    private lookSignature = (): string => JSON.stringify([this.entity.appearance, this.entity.inventory.items])

    private drawPalette(): Container {
        const column = new Container()
        this.addChild(column)
        const panel = new Graphics()
        drawGlassPanel(panel, PALETTE_X, BODY_Y, PALETTE_W, BODY_H)
        column.addChild(panel)

        const view = describeTrait(this.entity, this.openTrait)
        const x = PALETTE_X + PALETTE_PAD

        const heading = text(view.label, { role: "label", color: ACCENT, display: true, weight: "500", size: 17 })
        heading.x = x
        heading.y = BODY_Y + 9
        column.addChild(heading)

        const hint = text(`Now: ${view.summary}`, { role: "compactCaption", color: TEXT_DIM })
        fitText(hint, paletteInnerWidth() - heading.width - 14)
        hint.x = PALETTE_X + PALETTE_W - PALETTE_PAD - hint.width
        hint.y = BODY_Y + 15
        column.addChild(hint)

        const top = BODY_Y + PALETTE_TITLE_H
        const bottom = BODY_Y + BODY_H - PALETTE_PAD
        const colorSection = view.sections.find(section => section.kind === "color")
        const styleSection = view.sections.find(section => section.kind === "style")

        // The colour row is short and fixed; the style grid takes whatever is left.
        let colorBlockH = 0
        let colorLayout: GridLayout | undefined
        if (colorSection) {
            colorLayout = layoutGrid({
                count: colorSection.options.length,
                width: paletteInnerWidth(),
                height: COLOR_CELL * 2 + GRID_GAP,
                cellHeight: COLOR_CELL,
                minCellWidth: COLOR_CELL,
                maxCellWidth: COLOR_CELL + 6,
                gapX: GRID_GAP,
                gapY: GRID_GAP,
            })
            colorBlockH = SECTION_HEAD_H + colorLayout.height + SECTION_GAP
        }

        let y = top
        if (styleSection) {
            const available = bottom - top - colorBlockH - SECTION_HEAD_H - PAGER_H
            const layout = layoutGrid({
                count: styleSection.options.length,
                width: paletteInnerWidth(),
                height: available,
                cellHeight: STYLE_CELL_H,
                minCellWidth: STYLE_MIN_W,
                gapX: GRID_GAP,
                gapY: GRID_GAP,
            })
            y = this.drawSection(column, styleSection, x, y, layout)
        }

        if (colorSection && colorLayout) {
            // With a style grid above it the colour row sits on the floor of the panel; on its own
            // (skin, eyes) it belongs at the top, not stranded at the bottom of an empty panel.
            const colorY = styleSection ? Math.max(y, bottom - colorBlockH + SECTION_GAP) : top
            this.drawSection(column, colorSection, x, colorY, colorLayout)
        }
        return column
    }

    // Draws one section (heading, grid of choices, pager when it doesn't fit on a page) and returns
    // the y just below it.
    private drawSection(column: Container, section: TraitSection, x: number, y: number, layout: GridLayout): number {
        const heading = sectionHeading(section.title, paletteInnerWidth())
        heading.x = x
        heading.y = y
        column.addChild(heading)

        const gridY = y + SECTION_HEAD_H
        const page = Math.min(this.pageIndex[this.openTrait] || 0, layout.pages - 1)
        const shown = pageSlice(section.options, layout, page)
        const cellH = section.kind === "color" ? COLOR_CELL : STYLE_CELL_H

        shown.forEach((option, index) => {
            const at = cellPosition(index, layout, cellH, GRID_GAP, GRID_GAP)
            const onClick = () => this.choose(section, option.value)
            const chip: Container =
                section.kind === "color"
                    ? new ColorChip(option.swatch, {
                          width: layout.cellWidth,
                          height: cellH,
                          selected: option.value === section.selected,
                          onClick,
                      })
                    : new LabelChip(option.label, {
                          width: layout.cellWidth,
                          height: cellH,
                          selected: option.value === section.selected,
                          onClick,
                      })
            chip.x = x + at.x
            chip.y = gridY + at.y
            column.addChild(chip)

            // The chip that was just picked rings once, so the eye finds what changed.
            const choice = this.lastChoice
            if (
                choice &&
                choice.trait === this.openTrait &&
                choice.kind === section.kind &&
                choice.value === option.value &&
                Date.now() - choice.at < 400
            ) {
                const ring = new Graphics()
                column.addChild(ring)
                this.pulse = { g: ring, x: chip.x, y: chip.y, w: layout.cellWidth, h: cellH, ms: 0 }
            }
        })

        let bottom = gridY + layout.height + SECTION_GAP

        if (layout.pages > 1) {
            const pager = new Pager({
                page,
                pages: layout.pages,
                onPage: next => {
                    this.pageIndex[this.openTrait] = next
                    this.host.refresh()
                },
            })
            pager.x = x + paletteInnerWidth() - pager.boxWidth
            pager.y = gridY + layout.height + 4
            column.addChild(pager)
            bottom += PAGER_H
        }

        return bottom
    }

    private choose(section: TraitSection, value: string) {
        this.lastChoice = { trait: this.openTrait, kind: section.kind, value, at: Date.now() }
        applyTraitChoice(this.entity, this.openTrait, section.kind, value)
        // A different hairstyle (or cut of shirt) can offer a different set of colours, so the open
        // palette is rebuilt rather than patched.
        this.host.refresh()
    }

    private drawNav() {
        this.host.setNavButtons([
            {
                title: "🎲 Random look",
                callback: () => {
                    const appearance = randomAppearanceFor(this.entity, "human", this.entity.appearance.sex)
                    appearance.inventoryAppearanceHash = this.entity.appearance.inventoryAppearanceHash
                    this.entity.appearance = appearance
                    this.entity.appearance.eyes = this.entity.appearance.eyes || "normal"
                    this.entity.appearance.noseColor = this.entity.appearance.bodyColor
                    reconcileForSex(this.entity)
                    this.host.refresh()
                },
            },
            {
                title: "👕 Random outfit",
                callback: () => {
                    this.entity.inventory.items = this.rollOutfit(this.entity)
                    reconcileForSex(this.entity)
                    this.host.refresh()
                },
            },
            {
                title: "Continue ▸",
                primary: true,
                callback: () => {
                    this.host.submitCharacter(this.entity.appearance, this.entity.inventory)
                    this.host.resetNavButtons()
                    this.host.switchPage("journey")
                },
            },
        ])
    }
}
